/**
 * Ограниченная сессия «только отмена подписки» (решение оператора 2026-09-28).
 *
 * Ссылка «Отменить подписку» из писем и бота входит без кода, но бэкенд даёт
 * по ней сессию, которой открыта лишь цепочка удержания и отмены
 * (`SessionScope` на бэкенде, срок — час). Остальное API отвечает ей 403.
 * Фронт узнаёт об области из ответа `POST /auth/exchange-token` и держит
 * человека на отдельном экране отмены — без меню кабинета.
 *
 * Все выходы с этого экрана ведут к ОБЫЧНОМУ входу: ограниченный токен
 * стирается, а куда человек шёл («Друзья», оплата со скидкой) запоминается и
 * срабатывает после входа (`resolvePostAuthRoute`).
 *
 * Зависимостей у модуля нет намеренно: его импортирует роутер на старте.
 */

export const SUBSCRIPTION_CANCEL_SCOPE = 'subscription_cancel'

/**
 * Предложения удержания, которых на экране отмены нет (HK-IGRA.954): их
 * действие ограниченной сессии закрыто — `downgrade` снимает докупленные
 * устройства только после обычного входа с кодом.
 */
export const CANCEL_SCOPE_HIDDEN_OFFERS: readonly string[] = ['downgrade']

const SCOPE_STORAGE_KEY = 'igra_session_scope'
const AFTER_LOGIN_STORAGE_KEY = 'igra_after_login'
const TOKEN_STORAGE_KEY = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'

interface StoredScope {
  scope: string
  /** unix time, секунды */
  expires_at: number | null
}

function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) storage()?.removeItem(key)
    else storage()?.setItem(key, value)
  } catch {
    // Запрещённое хранилище: ограниченный режим просто не переживёт перезагрузку.
  }
}

/** Запомнить область токена из ответа обмена; `null` — обычная сессия. */
export function rememberSessionScope(scope: string | null | undefined, expiresAt?: number | null): void {
  if (!scope) {
    write(SCOPE_STORAGE_KEY, null)
    return
  }

  const value: StoredScope = { scope, expires_at: typeof expiresAt === 'number' ? expiresAt : null }
  write(SCOPE_STORAGE_KEY, JSON.stringify(value))
}

/** Область текущего токена; `null` — обычная сессия (или её нет). */
export function currentSessionScope(): string | null {
  const raw = read(SCOPE_STORAGE_KEY)
  if (!raw) return null

  try {
    const value = JSON.parse(raw) as Partial<StoredScope>
    return typeof value.scope === 'string' && value.scope !== '' ? value.scope : null
  } catch {
    return null
  }
}

export function isCancelScopeSession(): boolean {
  return currentSessionScope() === SUBSCRIPTION_CANCEL_SCOPE
}

/** Срок ограниченной сессии вышел (по часам браузера; сервер решает сам). */
export function cancelScopeExpired(now: number = Date.now()): boolean {
  const raw = read(SCOPE_STORAGE_KEY)
  if (!raw) return false

  try {
    const value = JSON.parse(raw) as Partial<StoredScope>
    return typeof value.expires_at === 'number' && value.expires_at * 1000 <= now
  } catch {
    return false
  }
}

/** Стереть ограниченный вход целиком: токен и признак области. */
export function dropCancelScopeSession(): void {
  write(TOKEN_STORAGE_KEY, null)
  write(SCOPE_STORAGE_KEY, null)
}

/**
 * Куда вести после обычного входа. Только внутренний путь: `//evil.test`
 * и полные адреса отбрасываются.
 */
export function rememberAfterLogin(path: string): void {
  if (!path.startsWith('/') || path.startsWith('//')) return
  write(AFTER_LOGIN_STORAGE_KEY, path)
}

/** Забрать путь «после входа» — одноразово. */
export function takeAfterLogin(): string | null {
  const path = read(AFTER_LOGIN_STORAGE_KEY)
  write(AFTER_LOGIN_STORAGE_KEY, null)

  return path && path.startsWith('/') && !path.startsWith('//') ? path : null
}
